import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { Plus, X, Trash2, ArrowUpRight, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  draft: "bg-anvi-100 text-anvi-700",
  issued: "bg-blue-50 text-blue-700",
  partial: "bg-amber-50 text-amber-800",
  received: "bg-emerald-50 text-emerald-800",
  closed: "bg-slate-100 text-slate-700",
};

function NewPOModal({ onClose, onCreated }) {
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [catalogueQuery, setCatalogueQuery] = useState("");
  const [form, setForm] = useState({
    vendor_id: "", project_id: "", expected_delivery: "",
    terms: "Payment 50% advance, 50% on delivery.", notes: "",
  });
  const [lines, setLines] = useState([
    { description: "", spec: "", qty: 1, unit: "nos", rate: 0, gst_percent: 18 },
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.vendorsList().then((r) => setVendors(r.data || []));
    api.projectsList().then((r) => setProjects(r.data || []));
  }, []);

  // Fetch the vendor's catalogue whenever they pick a vendor
  useEffect(() => {
    if (!form.vendor_id) { setPriceItems([]); return; }
    api.priceList(form.vendor_id).then((r) => setPriceItems(r.data || [])).catch(() => setPriceItems([]));
  }, [form.vendor_id]);

  const updateLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { description: "", spec: "", qty: 1, unit: "nos", rate: 0, gst_percent: 18 }]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  // Add a catalogue item as a new PO line (or fill the first blank line)
  const pickFromCatalogue = (item) => {
    setLines((prev) => {
      const blankIdx = prev.findIndex((l) => !l.description.trim());
      const line = {
        description: item.description, spec: item.spec || "",
        qty: 1, unit: item.unit, rate: item.rate, gst_percent: item.gst_percent,
      };
      if (blankIdx >= 0) {
        const next = [...prev]; next[blankIdx] = line; return next;
      }
      return [...prev, line];
    });
    toast.success(`Added "${item.description}" from rate card`);
  };

  const filteredCatalogue = useMemo(() => {
    const q = catalogueQuery.trim().toLowerCase();
    if (!q) return priceItems;
    return priceItems.filter((x) => [x.description, x.spec, x.category].some((f) => (f || "").toLowerCase().includes(q)));
  }, [priceItems, catalogueQuery]);

  const totals = useMemo(() => {
    let subtotal = 0, gst = 0;
    lines.forEach((l) => {
      const amt = Number(l.qty || 0) * Number(l.rate || 0);
      subtotal += amt;
      gst += (amt * Number(l.gst_percent || 0)) / 100;
    });
    return { subtotal, gst, grand: subtotal + gst };
  }, [lines]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) { toast.error("Select a vendor"); return; }
    const validLines = lines.filter((l) => l.description.trim() && Number(l.qty) > 0);
    if (validLines.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        vendor_id: form.vendor_id,
        project_id: form.project_id || null,
        expected_delivery: form.expected_delivery || null,
        terms: form.terms,
        notes: form.notes,
        line_items: validLines.map((l) => ({
          description: l.description, spec: l.spec, unit: l.unit,
          qty: Number(l.qty), rate: Number(l.rate), gst_percent: Number(l.gst_percent),
        })),
      };
      const res = await api.purchaseOrderCreate(payload);
      toast.success(`Draft ${res.data.po_number} created`);
      onCreated(res.data);
      onClose();
    } catch { toast.error("Could not create PO"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="create-po-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">New Purchase Order</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">Create a draft PO</div>
          </div>
          <button type="button" onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1" data-testid="close-po-modal"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Vendor *</label>
              <select required value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} data-testid="po-field-vendor"
                      className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
                <option value="">Select vendor…</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.category}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Link project (optional)</label>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} data-testid="po-field-project"
                      className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
                <option value="">— None —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Expected delivery</label>
              <input type="date" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })}
                     data-testid="po-field-expected"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div className="md:col-span-1">
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Terms</label>
              <input value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="po-field-terms"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Line items</div>
              <button type="button" onClick={addLine} data-testid="po-add-line"
                      className="text-xs flex items-center gap-1 text-anvi-terracotta hover:text-anvi-terracotta-dark">
                <Plus size={12} /> Add line
              </button>
            </div>

            {form.vendor_id && priceItems.length > 0 && (
              <div className="mb-3 border border-anvi-200 rounded-md bg-anvi-50/40 p-3" data-testid="catalogue-picker">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-[0.15em] uppercase text-anvi-500">Rate card · {priceItems.length} items</div>
                  <input value={catalogueQuery} onChange={(e) => setCatalogueQuery(e.target.value)}
                         placeholder="Search catalogue…" data-testid="catalogue-search"
                         className="text-xs px-2 py-1 border border-anvi-200 rounded-sm bg-white w-48 focus:outline-none focus:border-anvi-terracotta" />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {filteredCatalogue.map((item) => (
                    <button type="button" key={item.id} onClick={() => pickFromCatalogue(item)}
                            data-testid={`catalogue-pick-${item.id}`}
                            className="flex-none text-left px-3 py-2 bg-white border border-anvi-200 hover:border-anvi-terracotta rounded-md min-w-[220px] group">
                      <div className="text-xs text-anvi-900 group-hover:text-anvi-terracotta font-medium truncate">{item.description}</div>
                      <div className="text-[10px] text-anvi-500 mt-0.5">{item.category || item.unit}</div>
                      <div className="text-sm font-medium tabular-nums text-anvi-900 mt-1">{inr(item.rate)} <span className="text-[10px] text-anvi-500">/ {item.unit}</span></div>
                    </button>
                  ))}
                  {filteredCatalogue.length === 0 && (
                    <div className="text-xs text-anvi-500 py-3 px-2">No items match "{catalogueQuery}"</div>
                  )}
                </div>
              </div>
            )}
            {form.vendor_id && priceItems.length === 0 && (
              <div className="mb-3 border border-dashed border-anvi-200 rounded-md p-3 text-xs text-anvi-500" data-testid="catalogue-empty">
                This vendor has no rate card yet. Open the vendor to add items, or enter lines manually below.
              </div>
            )}
            <div className="border border-anvi-200 rounded-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-anvi-50/60">
                  <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500">
                    <th className="px-3 py-2 text-left font-medium">Description</th>
                    <th className="px-3 py-2 text-right font-medium w-16">Qty</th>
                    <th className="px-3 py-2 text-left font-medium w-16">Unit</th>
                    <th className="px-3 py-2 text-right font-medium w-24">Rate</th>
                    <th className="px-3 py-2 text-right font-medium w-16">GST %</th>
                    <th className="px-3 py-2 text-right font-medium w-24">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const amt = Number(l.qty || 0) * Number(l.rate || 0);
                    return (
                      <tr key={i} className="border-t border-anvi-100" data-testid={`po-line-${i}`}>
                        <td className="px-2 py-1.5">
                          <input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })}
                                 placeholder="Item description" data-testid={`po-line-desc-${i}`}
                                 className="w-full px-2 py-1 text-sm border border-transparent focus:border-anvi-terracotta focus:outline-none rounded-sm" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={l.qty} onChange={(e) => updateLine(i, { qty: e.target.value })}
                                 data-testid={`po-line-qty-${i}`}
                                 className="w-full px-2 py-1 text-sm text-right border border-transparent focus:border-anvi-terracotta focus:outline-none rounded-sm tabular-nums" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })}
                                 className="w-full px-2 py-1 text-sm border border-transparent focus:border-anvi-terracotta focus:outline-none rounded-sm" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={l.rate} onChange={(e) => updateLine(i, { rate: e.target.value })}
                                 data-testid={`po-line-rate-${i}`}
                                 className="w-full px-2 py-1 text-sm text-right border border-transparent focus:border-anvi-terracotta focus:outline-none rounded-sm tabular-nums" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={l.gst_percent} onChange={(e) => updateLine(i, { gst_percent: e.target.value })}
                                 className="w-full px-2 py-1 text-sm text-right border border-transparent focus:border-anvi-terracotta focus:outline-none rounded-sm tabular-nums" />
                        </td>
                        <td className="px-2 py-1.5 text-sm text-right tabular-nums text-anvi-900">{inr(amt)}</td>
                        <td className="px-1 py-1.5 text-center">
                          {lines.length > 1 && (
                            <button type="button" onClick={() => removeLine(i)} data-testid={`po-remove-line-${i}`} className="text-anvi-400 hover:text-red-600 p-1">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex justify-end">
              <div className="text-right space-y-0.5 text-sm">
                <div className="text-anvi-500">Subtotal: <span className="text-anvi-900 tabular-nums">{inr(totals.subtotal)}</span></div>
                <div className="text-anvi-500">GST: <span className="text-anvi-900 tabular-nums">{inr(totals.gst)}</span></div>
                <div className="font-heading text-xl text-anvi-900 tabular-nums" data-testid="po-preview-total">Total: {inr(totals.grand)}</div>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                      className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-anvi-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-po-form"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              {saving ? "Creating…" : "Create Draft PO"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PurchaseOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pos, setPOs] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);

  const canDelete = user && (user.role === "Owner" || user.role === "Admin");

  const load = useCallback(async () => {
    try {
      const r = await api.purchaseOrdersList();
      setPOs(r.data || []);
    } catch { toast.error("Could not load POs"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pos.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!q) return true;
      return [p.po_number, p.vendor_name, p.notes].some((f) => (f || "").toLowerCase().includes(q));
    });
  }, [pos, query, statusFilter]);

  const handleDelete = async (po) => {
    if (!window.confirm(`Delete ${po.po_number}? This will also remove any GRNs.`)) return;
    try {
      await api.purchaseOrderDelete(po.id);
      toast.success("PO deleted");
      setPOs((prev) => prev.filter((x) => x.id !== po.id));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not delete";
      toast.error(msg);
    }
  };

  return (
    <div className="px-10 pt-4 pb-10 animate-fade-in" data-testid="purchase-orders-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">Supply Chain</div>
          <h1 className="font-heading text-4xl text-anvi-900">Purchase Orders</h1>
          <p className="text-sm text-anvi-500 mt-2">{pos.length} PO{pos.length === 1 ? "" : "s"} · issue, receive goods (GRN), track partial deliveries</p>
        </div>
        <button onClick={() => setCreateOpen(true)} data-testid="new-po-btn"
                className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors">
          <Plus size={16} strokeWidth={1.5} /> New PO
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-anvi-400" strokeWidth={1.5} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by PO #, vendor, notes…"
                 data-testid="po-search"
                 className="w-full pl-9 pr-3 py-2 border border-anvi-200 rounded-md text-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {["all", "draft", "issued", "partial", "received", "closed"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`po-filter-${s}`}
                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors capitalize ${statusFilter === s ? "bg-anvi-900 text-white border-anvi-900" : "bg-white border-anvi-200 text-anvi-700 hover:border-anvi-terracotta"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-anvi-50/60">
            <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500">
              <th className="px-4 py-3 text-left font-medium">PO #</th>
              <th className="px-4 py-3 text-left font-medium">Vendor</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Grand Total</th>
              <th className="px-4 py-3 text-left font-medium">Expected</th>
              <th className="px-4 py-3 text-left font-medium">Created</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-anvi-100 hover:bg-anvi-50/40 cursor-pointer" onClick={() => navigate(`/purchase-orders/${p.id}`)} data-testid={`po-row-${p.id}`}>
                <td className="px-4 py-3 text-sm font-mono text-anvi-900">{p.po_number}</td>
                <td className="px-4 py-3 text-sm text-anvi-900">{p.vendor_name || "—"}</td>
                <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_TONE[p.status] || "bg-anvi-100 text-anvi-700"}`}>{p.status}</span></td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-anvi-900 font-medium">{inr(p.grand_total)}</td>
                <td className="px-4 py-3 text-sm text-anvi-600">{p.expected_delivery || "—"}</td>
                <td className="px-4 py-3 text-sm text-anvi-500">{p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN") : ""}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/purchase-orders/${p.id}`} className="text-anvi-500 hover:text-anvi-terracotta p-1" title="Open">
                      <ArrowUpRight size={14} />
                    </Link>
                    {canDelete && (p.status === "draft" || p.status === "closed") && (
                      <button onClick={() => handleDelete(p)} className="text-anvi-400 hover:text-red-600 p-1" title="Delete" data-testid={`delete-po-${p.id}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-anvi-500" data-testid="po-empty">
                {pos.length === 0 ? "No POs yet. Click 'New PO' to create your first draft." : "No POs match your filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {createOpen && <NewPOModal onClose={() => setCreateOpen(false)} onCreated={(p) => setPOs((prev) => [p, ...prev])} />}
    </div>
  );
}
